import React, { createContext, useCallback, useEffect, useState } from 'react';
import { omit } from 'lodash';
import uuid from 'uuid';

import { useAuth } from 'contexts/auth';
import { useChat } from 'contexts/chat';
import { useUser2 } from 'contexts/user2';
import chatService from 'services/chat';
import groupService from 'services/group';

const getDirect = (courseId, groupId, members, timestamp) => {
    return {
        id: uuid.v4(),
        active: true,
        created_on: timestamp,
        modified_on: timestamp,
        chat_type: 'direct',
        course_id: courseId,
        group_id: groupId,
        name: '',
        members,
    };
};

const PageContext = createContext();

const PageProvider = (props) => {
    const { auth } = useAuth();
    const { messagesToProcess, setMessagesToProcess, unsendMessage, readChannel } = useChat();
    const { currentGroup, instructorAccountUsers, studentAccountUsers } = useUser2();

    // data
    const [activeGroupId, setActiveGroupId] = useState(''); // the id of the group you are looking at
    const [channels, setChannels] = useState(null); // channels, null means loading
    const [directs, setDirects] = useState(null); // directs, null means loading
    const [latestMessages, setLatestMessages] = useState({}); // map of latest message for each direct
    const [activeChatId, setActiveChatId] = useState(''); // the id of the chat you are looking at
    const [messages, setMessages] = useState(null); // messages in the active chat, null means loading
    const [files, setFiles] = useState(null); // files in the active chat, null means loading

    // dialogs
    const [createChannelDialog, setCreateChannelDialog] = useState({ open: false });
    const [editChannelDialog, setEditChannelDialog] = useState({ open: false });
    const [manageMembersDialog, setManageMembersDialog] = useState({ open: false });
    const [alertDialog, setAlertDialog] = useState({
        open: false,
        message: '',
        description: '',
        note: '',
        variant: 'destructive',
        actionButtonText: '',
        cancelButtonText: 'Cancel',
        onAction: () => {},
        onCancel: () => setAlertDialog((current) => ({ ...current, open: false })),
        onClose: () => setAlertDialog((current) => ({ ...current, open: false })),
    });

    // ui
    const [search, setSearch] = useState(''); // search filter input
    const [channelDetailsOpen, setChannelDetailsOpen] = useState(false); // whether channel details are visible

    const getChannels = () => {
        if (!channels) return channels;
        return channels.filter(({ members }) => members.includes(auth.owner_account_id));
    };

    const getMessages = useCallback(() => {
        if (!messages) return messages;
        return messages
            .filter(({ active }) => active)
            .map((message) => {
                if (message.type === 'file') {
                    return { ...message, file: files?.find(({ id }) => id === message.file_id) };
                }
                return message;
            });
    }, [messages, files]);

    const deleteMessage = async (targetMessage) => {
        setMessages((current) => {
            return current.map((message) => {
                if (message.id !== targetMessage.id) return message;
                return {
                    ...message,
                    active: false,
                };
            });
        });
        if (targetMessage.file_id) {
            setFiles((current) => current.filter(({ id }) => id !== targetMessage.file_id));
        }
        if (latestMessages[targetMessage.channel_id].id === targetMessage.id) {
            const [message] = await chatService.listMessages(targetMessage.channel_id, null, null, 1, 1);
            if (message) {
                setLatestMessages((current) => ({
                    ...current,
                    [targetMessage.channel_id]: message,
                }));
            } else {
                setLatestMessages((current) => {
                    return omit(current, [targetMessage.channel_id]);
                });
            }
        }
        unsendMessage(targetMessage);
    };

    useEffect(() => {
        if (currentGroup?.id) {
            setActiveGroupId(currentGroup.id);
        }
    }, [currentGroup?.id]);

    // fetch channel chats
    // reset necessary state when team is unselected
    useEffect(() => {
        (async () => {
            if (activeGroupId) {
                const channels = await groupService.listChannels(
                    activeGroupId,
                    JSON.stringify({ chat_type: 'channel', members: auth.owner_account_id })
                );
                setChannels(channels);
                setActiveChatId(channels[0].id);
            } else {
                setChannels(null);
                setDirects(null);
                setActiveChatId('');
                setChannelDetailsOpen(false);
            }
        })();
    }, [activeGroupId]);

    // fetch direct chats, create them if they don't exist
    useEffect(() => {
        const currentGroupAccountUsers = studentAccountUsers
            ? studentAccountUsers.filter(({ group_id }) => group_id === activeGroupId)
            : null;

        if (activeGroupId && instructorAccountUsers && currentGroupAccountUsers) {
            const combined = [...currentGroupAccountUsers, ...instructorAccountUsers];
            (async () => {
                const members = combined.filter(({ owner_account_id }) => owner_account_id !== auth.owner_account_id);
                const directs = await groupService.listChannels(
                    activeGroupId,
                    JSON.stringify({
                        course_id: auth.course_id,
                        group_id: activeGroupId,
                        chat_type: 'direct',
                        $or: members.map((member) => {
                            return {
                                members: { $all: [auth.owner_account_id, member.owner_account_id] },
                            };
                        }),
                    })
                );
                setDirects(directs);
                const membersWithoutDirect = members.filter((member) => {
                    return !directs.find((direct) => direct.members.includes(member.owner_account_id));
                });
                await Promise.all(
                    membersWithoutDirect.map(async (member) => {
                        const payload = getDirect(
                            auth.course_id,
                            activeGroupId,
                            [auth.owner_account_id, member.owner_account_id],
                            new Date().getTime()
                        );
                        setDirects((current) => [...current, payload]);
                        return await groupService.createChannel(activeGroupId, payload);
                    })
                );
            })();
        }
    }, [auth, activeGroupId, instructorAccountUsers, studentAccountUsers]);

    // fetch messages and files for the active chat
    useEffect(() => {
        if (activeChatId) {
            (async () => {
                setFiles(null);
                setMessages(null);

                const files = await chatService.getChatFiles(activeChatId);
                const messages = await chatService.listMessages(activeChatId);

                setFiles(files);
                setMessages(messages);

                if (messages.length) {
                    const lastMessage = messages[messages.length - 1];
                    readChannel(activeChatId, lastMessage.id);
                }
            })();
        } else {
            setMessages(null);
        }
    }, [activeChatId]);

    // fetch latest message for each chat
    useEffect(() => {
        (async () => {
            if (channels && directs) {
                await Promise.all(
                    [...channels, ...directs].map(async ({ id }) => {
                        const [message] = await chatService.listMessages(id, null, null, null, 1);
                        if (message) {
                            setLatestMessages((current) => ({
                                ...current,
                                [id]: message,
                            }));
                        }
                    })
                );
            }
        })();
    }, [channels, directs]);

    // listen to incoming ws messages
    useEffect(() => {
        if (messagesToProcess.length) {
            const data = messagesToProcess[0];

            if (data.payload_type === 'send') {
                const message = data.payload;
                setMessagesToProcess((current) => current.filter(({ payload }) => payload.id !== message.id));

                // if the incoming message belongs to a channel that is not in `channels`, refetch channels
                if (message.group_id === activeGroupId && !channels.some(({ id }) => id === message.channel_id)) {
                    (async () => {
                        const channels = await groupService.listChannels(
                            activeGroupId,
                            JSON.stringify({ chat_type: 'channel', members: auth.owner_account_id })
                        );
                        setChannels(channels);
                    })();
                }

                // if the incoming message belongs to the active chat
                if (message.channel_id === activeChatId) {
                    (async () => {
                        if (message.type === 'file') {
                            const files = await chatService.getChatFiles(activeChatId);
                            setFiles(files);
                        }
                        setMessages((current) => [...current, message]);
                        readChannel(message.channel_id, message.id);
                    })();
                }

                setLatestMessages((current) => ({
                    ...current,
                    [message.channel_id]: message,
                }));
            }

            if (data.payload_type === 'delete') {
                const targetMessage = data.payload;
                setMessagesToProcess((current) => current.filter(({ payload }) => payload.id !== targetMessage.id));
                if (messages) {
                    setMessages((current) => {
                        return current.map((message) => {
                            if (message.id !== targetMessage.id) return message;
                            return {
                                ...message,
                                active: false,
                            };
                        });
                    });
                }
                if (latestMessages[targetMessage.channel_id]?.id === targetMessage.id) {
                    (async () => {
                        const [message] = await chatService.listMessages(targetMessage.channel_id, null, null, null, 1);
                        if (message) {
                            setLatestMessages((current) => ({
                                ...current,
                                [targetMessage.channel_id]: message,
                            }));
                        } else {
                            setLatestMessages((current) => {
                                omit(current, [targetMessage.channel_id]);
                            });
                        }
                    })();
                }
            }
        }
    }, [messagesToProcess, activeGroupId, channels]);

    const value = {
        // data
        activeGroupId,
        setActiveGroupId,
        channels: getChannels(),
        setChannels,
        directs,
        latestMessages,
        setLatestMessages,
        activeChatId,
        setActiveChatId,
        messages: getMessages(),
        setMessages,
        files,
        setFiles,
        deleteMessage,

        // dialogs
        createChannelDialog,
        setCreateChannelDialog,
        editChannelDialog,
        setEditChannelDialog,
        manageMembersDialog,
        setManageMembersDialog,
        alertDialog,
        setAlertDialog,

        // ui
        search,
        setSearch,
        channelDetailsOpen,
        setChannelDetailsOpen,
    };

    return <PageContext.Provider value={value} {...props} />;
};

export { PageContext };
export default PageProvider;
